import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/bh-mail-editor/client/tr-mails-template-editor.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { MailTemplate } from '@adornis/mails/db/mail-template.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { pageTitle } from '../../page-title.js';
import { getMatchingWeekMailTemplates } from '../../../api/mails.js';
import { getAndEnsureUserDataOfUserID } from '../../../api/user.js';
import { ResumeFile } from '../../../db/ResumeFile.js';
import { ResumeReviewFile } from '../../../db/ResumeReviewFile.js';
import { reviewCvFileQuery } from '../../../features/cv-review/api/reviewCvFileQuery.js';
import { CompanyApplicationDetail } from '../company/company-application-detail.js';
import { CompanyProfile } from '../company/company-profile.js';
import { companyJobRouter } from '../company/company-routes.js';
import { AdminApplicationPhase } from './admin-application-phase.js';
import { AdminCategories } from './admin-categories.js';
import { AdminCompanyDetail } from './admin-company-detail.js';
import { AdminCompanyList } from './admin-company-list.js';
import { AdminCreateAdmin } from './admin-create-admin.js';
import { AdminCreateCompany } from './admin-create-company.js';
import { AdminCreateCompanyUser } from './admin-create-user.js';
import { AdminDesignSettings } from './admin-design-settings.js';
import { AdminEditCompanyTerms } from './admin-edit-company-terms.js';
import { AdminEditImprint } from './admin-edit-imprint.js';
import { AdminEditPrivacy } from './admin-edit-privacy.js';
import { AdminUserCreate } from './admin-user-create.js';
import { AdminUserDetail } from './admin-user-detail.js';
import { AdminUserList } from './admin-user-list.js';
import { AdminEmailSettings } from './admin-email-settings.js';
import { AdminEmbedSnippets } from './admin-embed-snippets.js';
import { AdminRegistrationDetail } from './admin-registration-detail.js';
import { AdminRegistrationList } from './admin-registration-list.js';
import { AdminStatistics } from './admin-statistics.js';
import { getResumeFileByID } from '../../../api/resume-file.js';
import { getResumeReviewFileByID } from '../../../api/resume-review-file.js';

export const adminRouter = makeRouter(
  {
    root: {
      path: '/',
      documentTitlePart: pageTitle(m.companies),
      render: () => AdminCompanyList(),
    },
    categories: {
      path: '/categories',
      documentTitlePart: pageTitle(m.admin_categories_title),
      render: () => AdminCategories(),
    },
    statistics: {
      path: '/statistics',
      documentTitlePart: pageTitle(m.admin_statistics_title),
      render: () => AdminStatistics(),
    },
    editImprint: {
      path: '/legal/imprint',
      documentTitlePart: pageTitle(m.admin_edit_imprint_title),
      render: () => AdminEditImprint(),
    },
    editPrivacy: {
      path: '/legal/privacy',
      documentTitlePart: pageTitle(m.admin_edit_privacy_title),
      render: () => AdminEditPrivacy(),
    },
    editCompanyTerms: {
      path: '/legal/company-terms',
      documentTitlePart: pageTitle(m.admin_edit_company_terms_title),
      render: () => AdminEditCompanyTerms(),
    },
    registrations: {
      path: '/registrations',
      documentTitlePart: pageTitle(m.admin_registrations_sidebar),
      render: () => AdminRegistrationList(),
    },
    registrationDetail: {
      path: '/registration/:registrationID',
      documentTitlePart: pageTitle(m.page_title_registration),
      render: params => AdminRegistrationDetail({ registrationID: params.registrationID! }),
    },
    emailSettings: {
      path: '/email-settings',
      documentTitlePart: pageTitle(m.admin_email_settings_sidebar),
      render: () => AdminEmailSettings(),
    },
    embedSnippets: {
      path: '/embed',
      documentTitlePart: pageTitle(m.admin_nav_embed),
      render: () => AdminEmbedSnippets(),
    },
    designSettings: {
      path: '/design-settings',
      documentTitlePart: pageTitle(m.admin_design_settings_title),
      render: () => AdminDesignSettings(),
    },
    applicationPhase: {
      path: '/application-phase',
      documentTitlePart: pageTitle(m.admin_application_phase_title),
      render: () => AdminApplicationPhase(),
    },
    createCompany: {
      path: '/create-company',
      documentTitlePart: pageTitle(m.admin_create_company_title),
      render: () => AdminCreateCompany(),
    },
    users: {
      path: '/users',
      documentTitlePart: pageTitle(m.admin_user_list_title),
      render: () => AdminUserList(),
    },
    userCreate: {
      path: '/users/new',
      documentTitlePart: pageTitle(m.admin_user_create_title),
      render: () => AdminUserCreate(),
    },
    userDetail: {
      path: '/user/:userID',
      documentTitlePart: pageTitle(m.page_title_user),
      render: params => AdminUserDetail({ userID: params.userID! }),
    },
    testCv: {
      path: '/test-cv',
      documentTitlePart: pageTitle(m.admin_sidebar_test_cv),
      render: stateful(() => {
        if (baseConfig.get('DEPLOYMENT') === 'production') return nothing;
        const [triggerToUpdateQueries] = useState(false);
        const user = useQuery(() => currentUser(), [triggerToUpdateQueries]);
        if (user.loading || !user.value) return html` user not found `;
        const userData = useQuery(
          () => getAndEnsureUserDataOfUserID(user.value!._id)({ resumeFileId: 1 }),
          [user.value],
        );
        if (userData.loading || !userData.value) return html` user data not found `;
        const file = useQuery(
          () =>
            getResumeFileByID(userData.value?.resumeFileId ?? '')({
              _id: 1,
              reviewResult: { _class: 1 },
              reviewResultFileId: 1,
            }),
          [userData],
        );
        const reviewedFile = useQuery(
          () => getResumeReviewFileByID(file.value?.reviewResultFileId ?? '')({ _id: 1 }),
          [file.value],
        );
        if (file.loading || reviewedFile.loading) return html` file/data is loading `;

        return html`
          ${
            file.value
              ? html`
                  <x-button @click=${() => goTo(ResumeFile.getServeLink(file.value!._id), { target: '_blank' })}>
                    Lebenslauf ansehen
                  </x-button>
                  <x-button
                    @click=${async () => {
                      const action = async () => {
                        console.log({ reviewedFileId: file.value!.reviewResultFileId });
                        if (!file.value) return;
                        if (reviewedFile.value) {
                          await reviewedFile.value.remove();
                        }
                        if (file.value.reviewResultFileId) {
                          file.value.reviewResultFileId = null;
                          await file.value.save();
                        }
                        await reviewCvFileQuery(file.value._id)();
                        window.location.reload();
                      };

                      await XNativeDialog.waitFor(Promise.all([action()]), 'Datei wird reviewed...');
                    }}
                  >
                    Review Lebenslauf
                  </x-button>
                  <x-button
                    @click=${async () => {
                      const action = async () => {
                        if (!file.value) return;

                        const newFile = await askForFile({ acceptFileTypes: 'pdf', acceptMultipleFiles: false }).then(
                          files => files?.item(0),
                        );
                        if (!newFile) return;
                        await file.value.upload(newFile);
                        file.value.reviewResultFileId = null;
                        await file.value.save();
                      };

                      await XNativeDialog.waitFor(Promise.all([action()]), 'Neue Datei wird hochgeladen...');
                    }}
                  >
                    Neue Datei hochladen
                  </x-button>
                `
              : html`
                  <x-button
                    @click=${async () => {
                      const action = async () => {
                        const newFile = await askForFile({ acceptFileTypes: 'pdf', acceptMultipleFiles: false }).then(
                          files => files?.item(0),
                        );
                        if (!newFile) return;
                        const fileID = await ResumeFile.upload(newFile);
                        const ud = await getAndEnsureUserDataOfUserID(user.value!._id)({ _id: 1, resumeFileId: 1 });
                        ud.resumeFileId = fileID;
                        await ud.save();
                        window.location.reload();
                      };

                      await XNativeDialog.waitFor(Promise.all([action()]), 'Datei wird hochgeladen...');
                    }}
                  >
                    Datei hochladen
                  </x-button>
                `
          }
          ${
            reviewedFile.value
              ? html`
                  <x-button
                    @click=${() => goTo(ResumeReviewFile.getServeLink(reviewedFile.value!._id), { target: '_blank' })}
                  >
                    Bewertung ansehen
                  </x-button>
                `
              : nothing
          }
        `;
      }),
    },
    companyDetail: {
      path: '/company/:companyID',
      documentTitlePart: pageTitle(m.company),
      render: params => AdminCompanyDetail({ companyID: params.companyID! }),
    },
    createUserForCompany: {
      path: '/company/:companyID/create-user',
      documentTitlePart: pageTitle(m.admin_create_company_user_title),
      // ! this actually makes a difference compared to render: params => AdminCreateCompanyUser({ companyID: params.companyID! })
      // had the issue that state was not reset when reentering the create-user page
      render: params => html` ${AdminCreateCompanyUser({ companyID: params.companyID! })}`,
    },
    createAdmin: {
      path: '/create-admin',
      documentTitlePart: pageTitle(m.admin_create_title),
      render: () => AdminCreateAdmin(),
    },
    mailTemplates: {
      path: '/mail-templates',
      documentTitlePart: pageTitle(m.admin_sidebar_mail_templates),
      render: () =>
        html`<div class="p-4 sm:p-8 flex flex-col items-center w-full">
          <div class="w-full">
            <tr-mails-template-editor
              .getMailTemplates=${() => getMatchingWeekMailTemplates()(selectionSet(() => MailTemplate, 2))}
              footer
              attachments
              clearable
            ></tr-mails-template-editor>
          </div>
        </div>`,
    },
    editCompany: {
      path: '/company/:companyID/edit',
      documentTitlePart: pageTitle(m.company_profile_title),
      render: params =>
        html`<div class="p-4 sm:p-8 flex flex-col items-center">
          <div class="flex justify-between mb-6 w-full">
            <button
              @click=${() => adminRouter.goTo('companyDetail', { params: { companyID: params.companyID! } })}
              class="btn btn-ghost"
            >
              <span class="icon-[material-symbols--arrow-back]"></span>
              ${m.admin_create_company_user_back_to_company()}
            </button>
          </div>
          ${CompanyProfile({ companyID: params.companyID! })}
        </div> `,
    },
    jobDetail: {
      path: '/job/:companyID{/*}*',
      documentTitlePart: '',
      subRouter: companyJobRouter,
      render: ({ companyID }) => companyJobRouter.outlet.value({ companyID }),
    },
    application: {
      path: '/application/:applicationID',
      documentTitlePart: pageTitle(m.application_title),
      render: ({ applicationID }) => CompanyApplicationDetail({ applicationID: applicationID! }),
    },
  },
  { fallback: () => html`${m.page_not_found()}` },
);
